import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-[13px] font-semibold tracking-[0.02em] ring-offset-background transition-lux focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:translate-y-px",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-light shadow-soft hover:shadow-medium hover:-translate-y-0.5",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-soft hover:shadow-medium",
        outline:
          "border border-accent/40 bg-transparent text-foreground hover:border-accent hover:bg-accent/5 hover:-translate-y-0.5",
        secondary:
          "bg-secondary text-secondary-foreground border border-border hover:bg-secondary/70 hover:border-accent/40",
        ghost: "text-foreground/70 hover:text-foreground hover:bg-secondary/60",
        link: "text-primary underline-offset-4 hover:underline font-medium",
        ghana: "bg-primary text-primary-foreground border border-accent/40 hover:bg-primary-light hover:-translate-y-0.5 shadow-soft hover:shadow-medium font-semibold",
        hero: "bg-accent text-accent-foreground hover:brightness-[1.06] hover:-translate-y-0.5 shadow-soft hover:shadow-medium font-semibold border-0",
        success: "bg-success text-white hover:bg-success/90 shadow-soft border-0",
        premium: "bg-primary text-primary-foreground border border-accent/50 hover:bg-primary-light hover:-translate-y-0.5 shadow-medium font-semibold",
        glass: "bg-white/10 backdrop-blur-md border border-white/25 text-white hover:bg-white/20 shadow-soft",
        floating: "bg-card text-foreground border border-accent/25 hover:border-accent/50 hover:-translate-y-0.5 shadow-apple hover:shadow-card-hover rounded-sm",
      },
      size: {
        default: "h-10 px-5 py-2 max-md:min-h-[44px]",
        sm: "h-9 rounded-sm px-3.5 text-[12px] max-md:min-h-[44px]",
        lg: "h-12 rounded-sm px-8 text-sm",
        xl: "h-14 rounded-sm px-10 text-[15px] font-semibold tracking-[0.04em]",
        icon: "h-10 w-10 rounded-sm max-md:min-h-[44px] max-md:min-w-[44px]",
        "icon-sm": "h-8 w-8 rounded-sm max-md:min-h-[44px] max-md:min-w-[44px]",
        "icon-lg": "h-12 w-12 rounded-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
